import Check from '@mui/icons-material/Check';
import Edit from '@mui/icons-material/Edit';
import Flag from '@mui/icons-material/Flag';
import FlagOutlined from '@mui/icons-material/FlagOutlined';
import Divider from '@mui/material/Divider';
import { SxProps, Theme } from '@mui/material/styles';
import { t } from 'i18next';

import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';

import { FlexRow } from '../styled';
import { TicketDrawerBusyReason } from './constants';

interface TicketDetailsCTAsProps {
  busyReason?: TicketDrawerBusyReason;
  isEditingTicket?: boolean;
  isTickedFlagged?: boolean;
  onApproveLoadAndNextClick?: () => void;
  onApproveLoadClick?: () => void;
  onCancelClick?: () => void;
  onEditClick?: () => void;
  onSaveClick?: () => void;
  onTickedFlaggedStatusChangeClick?: () => void;
  onUnapproveLoadClick?: () => void;
  sx?: SxProps<Theme>;
}

export default function TicketDetailsCTAs({
  busyReason,
  isEditingTicket,
  isTickedFlagged,
  onApproveLoadAndNextClick,
  onApproveLoadClick,
  onCancelClick,
  onEditClick,
  onSaveClick,
  onTickedFlaggedStatusChangeClick,
  onUnapproveLoadClick,
  sx,
}: TicketDetailsCTAsProps) {
  const isBusy = Boolean(busyReason);

  const barSx: SxProps<Theme> = [
    { alignItems: 'center', justifyContent: 'flex-end', gap: 1 },
    ...(Array.isArray(sx) ? sx : [sx]),
  ];

  // Distinct keys per mode remount the bar on the read↔edit switch. Without
  // this React reuses the green Save node as Flag and its background-color
  // transition leaves Flag flashing green.
  if (isEditingTicket) {
    return (
      <FlexRow key="edit-actions" sx={barSx}>
        <SmallButton
          color="inherit"
          disabled={isBusy}
          onClick={onCancelClick}
          variant="outlined"
          sx={(theme) => ({
            bgcolor: 'background.paper',
            border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
          })}
        >
          {t('actions.cancel')}
        </SmallButton>

        <SmallLoadingButton
          color="brandV2Green"
          disabled={isBusy}
          loading={busyReason === TicketDrawerBusyReason.SAVING_TICKET}
          onClick={onSaveClick}
        >
          {t('actions.save_changes')}
        </SmallLoadingButton>
      </FlexRow>
    );
  }

  const hasFlagAction = Boolean(onTickedFlaggedStatusChangeClick);
  const hasOtherActions = Boolean(
    onEditClick ||
    onApproveLoadClick ||
    onUnapproveLoadClick ||
    onApproveLoadAndNextClick,
  );

  if (!hasFlagAction && !hasOtherActions) {
    return null;
  }

  return (
    <FlexRow key="read-actions" sx={barSx}>
      {onTickedFlaggedStatusChangeClick && (
        <SmallLoadingButton
          color="inherit"
          disabled={isBusy}
          loading={busyReason === TicketDrawerBusyReason.CHANGING_TICKET_FLAGGED_STATE}
          onClick={onTickedFlaggedStatusChangeClick}
          startIcon={isTickedFlagged ? <FlagOutlined /> : <Flag />}
          variant="outlined"
          sx={(theme) => ({
            bgcolor: 'background.paper',
            border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
          })}
        >
          {isTickedFlagged ? t('approvals.remove_flag') : t('actions.flag')}
        </SmallLoadingButton>
      )}

      {hasFlagAction && hasOtherActions && (
        <Divider orientation="vertical" sx={{ height: 24, mx: 0.5 }} />
      )}

      {onEditClick && (
        <SmallLoadingButton
          color="inherit"
          disabled={isBusy}
          onClick={onEditClick}
          startIcon={<Edit />}
          variant="outlined"
          sx={(theme) => ({
            bgcolor: 'background.paper',
            border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
          })}
        >
          {t('actions.edit')}
        </SmallLoadingButton>
      )}

      {onApproveLoadClick && (
        <SmallLoadingButton
          color="inherit"
          disabled={isBusy}
          loading={busyReason === TicketDrawerBusyReason.APPROVING_LOAD}
          onClick={onApproveLoadClick}
          startIcon={<Check />}
          variant="outlined"
          sx={(theme) => ({
            bgcolor: 'background.paper',
            border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
          })}
        >
          {t('actions.approve')}
        </SmallLoadingButton>
      )}

      {onUnapproveLoadClick && (
        <SmallLoadingButton
          color="inherit"
          disabled={isBusy}
          loading={busyReason === TicketDrawerBusyReason.UNAPPROVING_LOAD}
          onClick={onUnapproveLoadClick}
          sx={(theme) => ({
            bgcolor: 'background.paper',
            border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
          })}
        >
          {t('approvals.driver_day.unapprove')}
        </SmallLoadingButton>
      )}

      {onApproveLoadAndNextClick && (
        <SmallLoadingButton
          color="brandV2Green"
          disabled={isBusy}
          loading={busyReason === TicketDrawerBusyReason.APPROVING_LOAD_AND_NEXT}
          onClick={onApproveLoadAndNextClick}
          startIcon={<Check />}
        >
          {t('actions.approve_and_next')}
        </SmallLoadingButton>
      )}
    </FlexRow>
  );
}
